import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags } from '@lezer/highlight';

// Inherit the same light/black CSS variables as Flutter and the launch shell.
const colors = {
  bg: 'var(--genius-surface)',
  bgLight: 'var(--genius-background)',
  bgLighter: 'var(--genius-raised)',
  fg: 'var(--genius-ink)',
  fgDim: 'var(--genius-muted)',
  comment: 'var(--genius-muted)',
  lineNum: 'var(--genius-muted)',
  lineNumActive: 'var(--genius-ink)',
  selection: 'var(--genius-accent-soft)',
  cursor: 'var(--genius-ink)',
  keyword: 'var(--genius-accent)',
  string: 'var(--genius-success)',
  command: 'var(--genius-warning)',
  math: 'var(--genius-info)',
  bracket: 'var(--genius-accent)',
  number: 'var(--genius-danger)',
  operator: 'var(--genius-accent)',
  error: 'var(--genius-danger)',
  gutter: 'var(--genius-background)',
  activeLine: 'var(--genius-raised)',
  // Keep the active line translucent so selection remains visible behind text.
  activeLineContent: 'color-mix(in srgb, var(--genius-accent) 8%, transparent)',
};

export const genisEditorTheme = EditorView.theme({
  '&': {
    color: colors.fg,
    backgroundColor: colors.bg,
    fontSize: '14px',
    fontFamily: '"Genius Mono", monospace',
    height: '100%',
  },
  '.cm-content': {
    caretColor: colors.cursor,
    padding: '8px 0',
  },
  '.cm-cursor, .cm-dropCursor': {
    borderLeftColor: colors.cursor,
    borderLeftWidth: '2px',
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: colors.selection,
  },
  '.cm-panels': {
    backgroundColor: colors.bgLight,
    color: colors.fg,
  },
  '.cm-panels.cm-panels-top': {
    borderBottom: `1px solid ${colors.bgLighter}`,
  },
  '.cm-panels.cm-panels-bottom': {
    borderTop: `1px solid ${colors.bgLighter}`,
  },
  '.cm-searchMatch': {
    backgroundColor: 'var(--genius-warning-soft)',
    outline: `1px solid ${colors.command}`,
  },
  '.cm-searchMatch.cm-searchMatch-selected': {
    backgroundColor: 'var(--genius-accent-soft)',
  },
  '.cm-activeLine': {
    backgroundColor: colors.activeLineContent,
  },
  '.cm-selectionMatch': {
    backgroundColor: 'var(--genius-accent-soft)',
  },
  '&.cm-focused .cm-matchingBracket, &.cm-focused .cm-nonmatchingBracket': {
    outline: `1px solid ${colors.bracket}`,
    backgroundColor: 'var(--genius-accent-soft)',
  },
  '.cm-gutters': {
    backgroundColor: colors.gutter,
    color: colors.lineNum,
    border: 'none',
    borderRight: `1px solid ${colors.bgLighter}`,
  },
  '.cm-activeLineGutter': {
    backgroundColor: colors.activeLine,
    color: colors.lineNumActive,
  },
  '.cm-foldPlaceholder': {
    backgroundColor: colors.bgLighter,
    color: colors.fgDim,
    border: 'none',
    padding: '0 4px',
    borderRadius: '3px',
  },
  '.cm-tooltip': {
    backgroundColor: colors.bg,
    color: colors.fg,
    border: `1px solid ${colors.bgLighter}`,
    borderRadius: '6px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  '.cm-tooltip .cm-tooltip-arrow:before': {
    borderTopColor: colors.bgLighter,
    borderBottomColor: colors.bgLighter,
  },
  '.cm-tooltip .cm-tooltip-arrow:after': {
    borderTopColor: colors.bg,
    borderBottomColor: colors.bg,
  },
  '.cm-tooltip-autocomplete': {
    '& > ul > li[aria-selected]': {
      backgroundColor: colors.bgLighter,
      color: colors.fg,
    },
  },
  // Error line highlighting
  '.cm-error-line': {
    backgroundColor: 'var(--genius-danger-soft)',
    borderLeft: `3px solid ${colors.error}`,
  },
  // SyncTeX highlight line (forward sync)
  '.cm-synctex-highlight': {
    backgroundColor: 'var(--genius-warning-soft)',
    transition: 'background-color 0.3s',
  },
  // Yjs collaboration cursors
  '.cm-ySelectionInfo': {
    fontSize: '11px',
    fontFamily: '"Genius Reading", sans-serif',
    padding: '1px 6px',
    borderRadius: '3px 3px 3px 0',
    opacity: '1',
    transition: 'opacity 0.3s',
  },
}, { dark: false });

export const genisHighlightStyle = syntaxHighlighting(HighlightStyle.define([
  { tag: tags.keyword, color: colors.keyword, fontWeight: 'bold' },
  { tag: tags.comment, color: colors.comment, fontStyle: 'italic' },
  { tag: tags.string, color: colors.string },
  { tag: tags.number, color: colors.number },
  { tag: tags.operator, color: colors.operator },
  { tag: tags.bracket, color: colors.bracket },
  { tag: tags.meta, color: colors.command },
  { tag: tags.tagName, color: colors.command },
  { tag: tags.attributeName, color: colors.math },
  { tag: tags.attributeValue, color: colors.string },
  { tag: tags.variableName, color: colors.fg },
  { tag: tags.typeName, color: colors.keyword },
  { tag: tags.definition(tags.variableName), color: colors.command },
  { tag: tags.propertyName, color: colors.math },
  { tag: tags.heading, color: colors.keyword, fontWeight: 'bold' },
  { tag: tags.emphasis, fontStyle: 'italic' },
  { tag: tags.strong, fontWeight: 'bold' },
  { tag: tags.invalid, color: colors.error },
]));
